import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import { ArrowLeft, MapPin, Clock, Beer, Download, Share2, Star, User, Edit, MessageSquare, Plus, Play, Calendar, Navigation, Loader2, Cloud, Route, Trash2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { StopWeather } from "@/components/WeatherCard";
import RollingTimePicker from "@/components/RollingTimePicker";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const TripDetail = () => {
  const navigate = useNavigate();
  const { tripId } = useParams();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStop, setSelectedStop] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [newRating, setNewRating] = useState(5);
  const [newComment, setNewComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [dayStats, setDayStats] = useState({});
  const [calculatingStats, setCalculatingStats] = useState(false);
  const [tripWeather, setTripWeather] = useState({});
  const [showWeather, setShowWeather] = useState(true); // Auto-show weather
  const [departureModalOpen, setDepartureModalOpen] = useState(false);
  const [departureDate, setDepartureDate] = useState("");
  const [departureTime, setDepartureTime] = useState("");
  const [loadingForecast, setLoadingForecast] = useState(false);
  const [forecastMode, setForecastMode] = useState(false); // true = showing forecast, false = current weather
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchTrip();
    loadGoogleMaps();
  }, [tripId]);

  useEffect(() => {
    if (trip?.stops?.length > 0 && window.google) {
      calculateDayDistances();
    }
  }, [trip]);

  // Load Google Maps script
  const loadGoogleMaps = () => {
    if (window.google && window.google.maps) return;
    
    const existingScript = document.getElementById("google-maps-script");
    if (existingScript) return;

    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (trip?.stops?.length > 0) {
        calculateDayDistances();
      }
    };
    document.head.appendChild(script);
  };

  const fetchTrip = async () => {
    try {
      const response = await axios.get(`${API}/trips/${tripId}`);
      setTrip(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load trip");
      navigate('/discover');
    } finally {
      setLoading(false);
    }
  };

  const fetchTripWeather = async () => {
    try {
      const response = await axios.get(`${API}/weather/trip/${tripId}`);
      const weatherMap = {};
      response.data.weather.forEach(w => {
        weatherMap[w.stop_id] = w;
      });
      setTripWeather(weatherMap);
      setForecastMode(false);
    } catch (error) {
      console.error("Failed to fetch weather:", error);
    }
  };

  const fetchForecastWeather = async (dateTime) => {
    setLoadingForecast(true);
    try {
      const response = await axios.post(`${API}/weather/trip/${tripId}/on-the-way`, {
        departure_time: dateTime
      });
      const weatherMap = {};
      response.data.weather.forEach(w => {
        weatherMap[w.stop_id] = w;
      });
      setTripWeather(weatherMap);
      setForecastMode(true);
      setDepartureModalOpen(false);
      toast.success("Weather forecast loaded for your trip!");
    } catch (error) {
      console.error("Failed to fetch forecast:", error);
      toast.error("Failed to load forecast");
    } finally {
      setLoadingForecast(false);
    }
  };

  const handleSetDeparture = () => {
    if (!departureDate || !departureTime) {
      toast.error("Please select date and time");
      return;
    }
    const dateTime = `${departureDate}T${departureTime}:00`;
    fetchForecastWeather(dateTime);
  };

  // Auto-fetch current weather when trip loads
  useEffect(() => {
    if (trip?.stops?.length > 0 && Object.keys(tripWeather).length === 0) {
      fetchTripWeather();
    }
  }, [trip]);

  // Calculate distances for each day using Google Maps Directions API
  const calculateDayDistances = async () => {
    if (!window.google || !trip?.stops) return;
    
    setCalculatingStats(true);
    const days = getStopsByDay();
    const stats = {};
    
    try {
      const directionsService = new window.google.maps.DirectionsService();
      
      for (let i = 0; i < days.length; i++) {
        const day = days[i];
        const dayStops = day.stops.filter(s => s.coordinates?.lat);
        
        if (dayStops.length === 0) {
          stats[day.dayNumber] = { distance: 0, duration: 0, stopTime: 0 };
          continue;
        }

        // Determine origin point
        let origin;
        if (day.dayNumber === 1) {
          // Day 1: Start from starting_location or first stop
          if (trip.starting_location?.lat) {
            origin = { lat: trip.starting_location.lat, lng: trip.starting_location.lng };
          } else {
            origin = { lat: dayStops[0].coordinates.lat, lng: dayStops[0].coordinates.lng };
          }
        } else {
          // Day 2+: Start from previous day's last stop (End of Day stop)
          const prevDay = days[i - 1];
          const prevDayLastStop = prevDay.stops[prevDay.stops.length - 1];
          if (prevDayLastStop?.coordinates?.lat) {
            origin = { lat: prevDayLastStop.coordinates.lat, lng: prevDayLastStop.coordinates.lng };
          } else {
            origin = { lat: dayStops[0].coordinates.lat, lng: dayStops[0].coordinates.lng };
          }
        }

        // Destination is last stop of the day
        const lastStop = dayStops[dayStops.length - 1];
        const destination = { lat: lastStop.coordinates.lat, lng: lastStop.coordinates.lng };

        // Waypoints are intermediate stops
        const waypoints = dayStops.slice(0, -1).map(stop => ({
          location: { lat: stop.coordinates.lat, lng: stop.coordinates.lng },
          stopover: true
        }));

        // Only calculate if we have different origin and destination
        if (origin.lat === destination.lat && origin.lng === destination.lng && waypoints.length === 0) {
          stats[day.dayNumber] = { distance: 0, duration: 0, stopTime: getTotalStopTime(day.stops) };
          continue;
        }

        try {
          const result = await new Promise((resolve, reject) => {
            directionsService.route({
              origin,
              destination,
              waypoints: waypoints.length > 0 ? waypoints : undefined,
              travelMode: window.google.maps.TravelMode.DRIVING,
              optimizeWaypoints: false
            }, (response, status) => {
              if (status === 'OK') {
                resolve(response);
              } else {
                reject(status);
              }
            });
          });

          // Sum up all legs
          let totalDistance = 0;
          let totalDuration = 0;
          result.routes[0].legs.forEach(leg => {
            totalDistance += leg.distance.value; // meters
            totalDuration += leg.duration.value; // seconds
          });

          stats[day.dayNumber] = {
            distance: Math.round(totalDistance / 1609.34), // Convert to miles
            duration: Math.round(totalDuration / 60), // Convert to minutes
            stopTime: getTotalStopTime(day.stops)
          };
        } catch (err) {
          console.log(`Could not calculate route for day ${day.dayNumber}:`, err);
          stats[day.dayNumber] = { distance: 0, duration: 0, stopTime: getTotalStopTime(day.stops) };
        }
      }
    } catch (err) {
      console.error("Error calculating distances:", err);
    }
    
    setDayStats(stats);
    setCalculatingStats(false);
    
    // Save total distance to trip for dashboard display (only if user owns the trip)
    const totalMiles = Object.values(stats).reduce((sum, s) => sum + (s.distance || 0), 0);
    if (totalMiles > 0 && user && trip?.user_id === user.user_id) {
      try {
        await axios.put(`${API}/trips/${tripId}`, {
          title: trip.title,
          description: trip.description,
          is_public: trip.is_public,
          cover_image: trip.cover_image,
          starting_location: trip.starting_location,
          total_distance: totalMiles
        });
      } catch (err) {
        // Silent fail - this is just for caching
      }
    }
  };

  // Get total stop time for a day (excluding End of Day markers)
  const getTotalStopTime = (stops) => {
    return stops.reduce((acc, s) => {
      if (s.estimated_time === -1) return acc;
      return acc + (s.estimated_time || 0);
    }, 0);
  };

  const fetchReviews = async (stopId) => {
    try {
      const response = await axios.get(`${API}/reviews/${stopId}`);
      setReviews(response.data.reviews || []);
    } catch (error) {
      console.error("Failed to load reviews");
    }
  };

  const handleStopClick = (stop) => {
    setSelectedStop(stop);
    fetchReviews(stop.stop_id);
  };

  const handleSubmitReview = async () => {
    if (!user) {
      toast.error("Please sign in to leave a review");
      return;
    }

    setSubmittingReview(true);
    try {
      const response = await axios.post(`${API}/reviews`, {
        stop_id: selectedStop.stop_id,
        rating: newRating,
        comment: newComment
      });
      setReviews([response.data, ...reviews]);
      setReviewModalOpen(false);
      setNewComment("");
      setNewRating(5);
      toast.success("Review added!");
    } catch (error) {
      toast.error("Failed to submit review");
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API}/trips/${tripId}/export?format=${format}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${trip.title}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: trip.title, url });
      } catch (e) {
        // User cancelled
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copied!");
    }
  };

  const getCategoryEmoji = (category) => {
    switch (category) {
      case 'dive_bar': return '🍺';
      case 'restaurant': return '🍔';
      case 'landmark': return '🗿';
      case 'hotel': return '🏨';
      case 'gas_station': return '⛽';
      default: return '📍';
    }
  };

  // Group stops by day based on "End of Day" markers (-1)
  const getStopsByDay = () => {
    if (!trip?.stops || trip.stops.length === 0) return [];
    
    const sortedStops = [...trip.stops].sort((a, b) => a.order - b.order);
    const days = [];
    let currentDay = [];
    let dayNumber = 1;

    sortedStops.forEach((stop, idx) => {
      currentDay.push(stop);
      
      // If this stop is "End of Day", start a new day for subsequent stops
      if (stop.estimated_time === -1 && idx < sortedStops.length - 1) {
        days.push({ dayNumber, stops: currentDay });
        dayNumber++;
        currentDay = [];
      }
    });

    // Push remaining stops
    if (currentDay.length > 0) {
      days.push({ dayNumber, stops: currentDay });
    }

    return days;
  };

  // Get day stats - use calculated values if available, otherwise estimate
  const getDayStats = (dayStops, dayNumber) => {
    // If we have calculated stats, use them
    if (dayStats[dayNumber]) {
      const { distance, duration, stopTime } = dayStats[dayNumber];
      return {
        totalTime: duration + stopTime,
        driveTime: duration,
        estimatedMiles: distance,
        stopTime
      };
    }
    
    // Fallback to estimates
    const stopTime = dayStops.reduce((acc, s) => {
      if (s.estimated_time === -1) return acc;
      return acc + (s.estimated_time || 0);
    }, 0);
    
    const stopsWithLocation = dayStops.filter(s => s.coordinates?.lat);
    let estimatedMiles = Math.max(0, (stopsWithLocation.length - 1) * 30);
    
    // For Day 1, add distance from starting location
    if (dayNumber === 1 && trip?.starting_location?.lat && stopsWithLocation.length > 0) {
      estimatedMiles += 30;
    }
    
    const driveTime = dayNumber === 1 && trip?.starting_location ? 60 : 0;
    
    return { 
      totalTime: stopTime + driveTime, 
      driveTime,
      estimatedMiles,
      stopTime
    };
  };

  const formatDuration = (minutes) => {
    if (minutes === -1) return 'End of Day';
    if (minutes < 60) return `${minutes} min`;
    if (minutes === 60) return '1 hour';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  // Get total number of days
  const getTotalDays = () => {
    const days = getStopsByDay();
    return days.length;
  };

  // Get total miles for entire trip
  const getTotalMiles = () => {
    let total = 0;
    const days = getStopsByDay();
    days.forEach((day) => {
      const stats = dayStats[day.dayNumber];
      if (stats) {
        total += stats.distance;
      }
    });
    return total;
  };

  // Get total drive time for entire trip
  const getTotalDriveTime = () => {
    let total = 0;
    const days = getStopsByDay();
    days.forEach((day) => {
      const stats = dayStats[day.dayNumber];
      if (stats) {
        total += stats.duration;
      }
    });
    return total;
  };

  // Delete trip
  const handleDeleteTrip = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/trips/${tripId}`);
      toast.success("Trip deleted");
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete trip");
    } finally {
      setDeleting(false);
      setDeleteModalOpen(false);
    }
  };

  const isOwner = user && trip?.user_id === user.user_id;

  if (loading) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-bronze">Loading...</div>
      </div>
    );
  }

  if (!trip) return null;

  const totalTime = trip.stops?.reduce((acc, s) => acc + (s.estimated_time || 0), 0) || 0;

  return (
    <div className="mobile-container asphalt-texture min-h-screen pb-6">
      {/* Header */}
      <div className="relative">
        <img
          src={trip.cover_image || "https://images.unsplash.com/photo-1610608498345-7e263b93b93e?w=600"}
          alt="Route"
          className="w-full h-48 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-[#050505]" />
        
        <div className="absolute top-4 left-4 right-4 flex justify-between">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            className="rounded-full bg-black/50 hover:bg-black/70"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          {isOwner && (
            <div className="flex gap-2">
              <Button
                data-testid="edit-trip-btn"
                variant="ghost"
                size="icon"
                className="rounded-full bg-black/50 hover:bg-black/70"
                onClick={() => navigate(`/trip/${tripId}/edit`)}
              >
                <Edit className="w-5 h-5" />
              </Button>
              <Button
                data-testid="delete-trip-btn"
                variant="ghost"
                size="icon"
                className="rounded-full bg-black/50 hover:bg-red-900/70"
                onClick={() => setDeleteModalOpen(true)}
              >
                <Trash2 className="w-5 h-5 text-red-400" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Trip Info */}
      <div className="px-4 -mt-8 relative z-10">
        <div className="glass rounded-xl p-4 mb-4">
          <h1 className="font-oswald text-2xl font-bold uppercase mb-2">{trip.title}</h1>
          {trip.description && (
            <p className="text-gray-400 text-sm mb-3">{trip.description}</p>
          )}
          
          {/* Starting Location */}
          {trip.starting_location && (
            <div className="flex items-center gap-2 mb-3 p-2 bg-green-500/10 rounded-lg border border-green-500/20">
              <Navigation className="w-4 h-4 text-green-500" />
              <div>
                <span className="text-xs text-green-400 uppercase">Starting from</span>
                <p className="text-sm text-white">{trip.starting_location.name}</p>
              </div>
            </div>
          )}
          
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              <span>{trip.stops?.length || 0} stops</span>
            </div>
            <div className="flex items-center gap-1 text-cyan-400">
              <Calendar className="w-4 h-4" />
              <span>{getTotalDays()} {getTotalDays() === 1 ? 'day' : 'days'}</span>
            </div>
            <div className="flex items-center gap-1 text-green-400">
              <Navigation className="w-4 h-4" />
              {calculatingStats ? (
                <span className="flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  calculating...
                </span>
              ) : (
                <span>{getTotalMiles()} mi</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <User className="w-4 h-4" />
              <span>{trip.creator?.name || 'Anonymous'}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <Button
            data-testid="weather-toggle-btn"
            variant="outline"
            className={`h-11 border-white/20 hover:bg-white/5 font-oswald uppercase text-xs px-2 ${forecastMode ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400' : ''}`}
            onClick={() => setDepartureModalOpen(true)}
          >
            <Route className="w-4 h-4 mr-1" />
            Weather
          </Button>
          <Button
            data-testid="export-csv-btn"
            variant="outline"
            className="h-11 border-white/20 hover:bg-white/5 font-oswald uppercase text-xs px-2"
            onClick={() => handleExport('csv')}
          >
            <Download className="w-4 h-4 mr-1" />
            CSV
          </Button>
          <Button
            data-testid="export-gpx-btn"
            variant="outline"
            className="h-11 border-white/20 hover:bg-white/5 font-oswald uppercase text-xs px-2"
            onClick={() => handleExport('gpx')}
          >
            <Download className="w-4 h-4 mr-1" />
            GPX
          </Button>
          <Button
            data-testid="share-btn"
            className="h-11 bg-cyan-500 hover:bg-cyan-600 font-oswald uppercase text-xs px-2"
            onClick={handleShare}
          >
            <Share2 className="w-4 h-4 mr-1" />
            Share
          </Button>
        </div>

        {/* Start Ride Button */}
        {user && trip.stops?.length > 0 && (
          <Button
            data-testid="start-ride-btn"
            className="w-full h-14 bg-bronze hover:bg-bronze-600 font-oswald text-lg uppercase tracking-wider neon-bronze btn-primary mb-6"
            onClick={() => navigate(`/trip/${tripId}/ride`)}
          >
            <Play className="w-5 h-5 mr-2" />
            Start Ride
          </Button>
        )}

        {/* Stops List */}
        <h2 className="font-oswald text-lg uppercase tracking-wide mb-3">Route Stops</h2>
        
        {(!trip.stops || trip.stops.length === 0) ? (
          <div className="glass rounded-xl p-6 text-center">
            <MapPin className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No stops on this route yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {getStopsByDay().map((day) => {
              const stats = getDayStats(day.stops, day.dayNumber);
              const sortedAllStops = trip.stops.sort((a, b) => a.order - b.order);
              const isCalculating = calculatingStats && !dayStats[day.dayNumber];
              
              return (
                <div key={day.dayNumber}>
                  {/* Day Header - always show if multiple days, or show "Day 1" header with stats */}
                  {getStopsByDay().length > 1 || day.dayNumber === 1 ? (
                    <div className="mb-3">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="h-px flex-1 bg-bronze/30" />
                        <span className="font-oswald text-sm uppercase tracking-wider text-bronze flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          Day {day.dayNumber}
                        </span>
                        <div className="h-px flex-1 bg-bronze/30" />
                      </div>
                      <div className="flex justify-center gap-3 text-xs text-gray-500">
                        {isCalculating ? (
                          <span className="flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Calculating route...
                          </span>
                        ) : (
                          <>
                            <span className="flex items-center gap-1" title="Drive time">
                              <Navigation className="w-3 h-3 text-cyan-400" />
                              {stats.driveTime > 0 ? formatDuration(stats.driveTime) : '0 min'} drive
                            </span>
                            <span className="flex items-center gap-1" title="Stop time">
                              <Clock className="w-3 h-3 text-bronze" />
                              {formatDuration(stats.stopTime)} stops
                            </span>
                            <span className="flex items-center gap-1" title="Distance">
                              <MapPin className="w-3 h-3 text-green-400" />
                              {stats.estimatedMiles} mi
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  ) : null}

                  {/* Day Stops */}
                  <div className="space-y-2">
                    {day.stops.map((stop) => {
                      const globalIndex = sortedAllStops.findIndex(s => s.stop_id === stop.stop_id);
                      
                      // If this is an "End of Day" marker
                      if (stop.estimated_time === -1) {
                        return (
                          <div
                            key={stop.stop_id}
                            data-testid={`stop-detail-${stop.stop_id}`}
                            className="glass rounded-xl p-4 border-purple-500/30 bg-purple-500/5"
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-500/20 text-purple-400 font-bold text-sm">
                                {globalIndex + 1}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-lg">🌙</span>
                                  <span className="font-semibold text-purple-300">{stop.name}</span>
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-xs text-purple-400/70">
                                  <span>End of Day {day.dayNumber}</span>
                                  {stop.address && (
                                    <span className="truncate max-w-[180px]">{stop.address}</span>
                                  )}
                                </div>
                                {stop.notes && (
                                  <p className="text-xs text-purple-400/50 mt-2 line-clamp-2">{stop.notes}</p>
                                )}
                                {/* Weather for End of Day stop */}
                                {showWeather && tripWeather[stop.stop_id] && (
                                  <div className="mt-2">
                                    <StopWeather weather={tripWeather[stop.stop_id]} compact />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={stop.stop_id}
                          data-testid={`stop-detail-${stop.stop_id}`}
                          className="glass rounded-xl p-4 cursor-pointer hover:border-bronze/50 transition-colors"
                          onClick={() => handleStopClick(stop)}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-bronze/20 text-bronze font-bold text-sm">
                              {globalIndex + 1}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">{getCategoryEmoji(stop.category)}</span>
                                <span className="font-semibold">{stop.name}</span>
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatDuration(stop.estimated_time)}
                                </span>
                                {stop.address && (
                                  <span className="truncate max-w-[180px]">{stop.address}</span>
                                )}
                              </div>
                              {stop.notes && (
                                <p className="text-xs text-gray-500 mt-2 line-clamp-2">{stop.notes}</p>
                              )}
                              {/* Weather for this stop */}
                              {showWeather && tripWeather[stop.stop_id] && (
                                <div className="mt-2">
                                  <StopWeather weather={tripWeather[stop.stop_id]} compact />
                                </div>
                              )}
                            </div>
                            <MessageSquare className="w-4 h-4 text-gray-600" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Stop Detail Modal */}
      <Dialog open={!!selectedStop} onOpenChange={() => setSelectedStop(null)}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-sm mx-4 max-h-[80vh] overflow-y-auto">
          {selectedStop && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-oswald uppercase">
                  <span className="text-2xl">{getCategoryEmoji(selectedStop.category)}</span>
                  {selectedStop.name}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 pt-4">
                {/* Stop Info */}
                <div className="glass rounded-lg p-3">
                  <div className="flex items-center gap-3 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {selectedStop.estimated_time} min
                    </span>
                    {selectedStop.coordinates?.lat && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {selectedStop.coordinates.lat.toFixed(4)}, {selectedStop.coordinates.lng.toFixed(4)}
                      </span>
                    )}
                  </div>
                  {selectedStop.address && (
                    <p className="text-sm text-gray-400 mt-2">{selectedStop.address}</p>
                  )}
                  {selectedStop.notes && (
                    <p className="text-sm text-gray-300 mt-2">{selectedStop.notes}</p>
                  )}
                </div>

                {/* Reviews Section */}
                <div className="flex justify-between items-center">
                  <h3 className="font-oswald uppercase text-sm">Reviews</h3>
                  <Button
                    data-testid="add-review-btn"
                    variant="ghost"
                    size="sm"
                    className="text-bronze hover:text-bronze-300"
                    onClick={() => setReviewModalOpen(true)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Review
                  </Button>
                </div>

                {reviews.length === 0 ? (
                  <div className="text-center py-4">
                    <Star className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">No reviews yet</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {reviews.map((review) => (
                      <div key={review.review_id} className="glass rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <Star
                                key={s}
                                className={`w-3 h-3 ${s <= review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}`}
                              />
                            ))}
                          </div>
                          <span className="text-xs text-gray-500">{review.user_name}</span>
                        </div>
                        {review.comment && (
                          <p className="text-sm text-gray-400">{review.comment}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Review Modal */}
      <Dialog open={reviewModalOpen} onOpenChange={setReviewModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="font-oswald uppercase">Add Review</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div>
              <p className="text-xs text-gray-400 uppercase mb-2">Rating</p>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    data-testid={`rating-star-${s}`}
                    onClick={() => setNewRating(s)}
                    className="p-1"
                  >
                    <Star
                      className={`w-8 h-8 transition-colors ${s <= newRating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600 hover:text-yellow-500/50'}`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs text-gray-400 uppercase mb-2">Comment (optional)</p>
              <Textarea
                data-testid="review-comment-input"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Great spot, cold beers..."
                className="bg-[#121212] border-white/10 min-h-[80px]"
              />
            </div>

            <Button
              data-testid="submit-review-btn"
              className="w-full h-11 bg-bronze hover:bg-bronze-600 font-oswald uppercase tracking-wider"
              onClick={handleSubmitReview}
              disabled={submittingReview}
            >
              {submittingReview ? 'Submitting...' : 'Submit Review'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Departure Time Modal */}
      <Dialog open={departureModalOpen} onOpenChange={setDepartureModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="font-oswald uppercase flex items-center gap-2">
              <Route className="w-5 h-5 text-cyan-400" />
              On the Way
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <p className="text-sm text-gray-400">
              Set your departure date and time to see weather forecasts for when you'll arrive at each stop.
            </p>

            <div>
              <p className="text-xs text-gray-400 uppercase mb-2">Departure Date</p>
              <Input
                type="date"
                value={departureDate}
                onChange={(e) => setDepartureDate(e.target.value)}
                className="h-12 bg-[#121212] border-white/10 focus:border-cyan-500"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div>
              <p className="text-xs text-gray-400 uppercase mb-2">Departure Time</p>
              <RollingTimePicker 
                value={departureTime || "09:00"} 
                onChange={(time) => setDepartureTime(time)} 
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 h-11 border-white/20"
                onClick={() => {
                  setDepartureDate("");
                  setDepartureTime("09:00");
                  setForecastMode(false);
                  fetchTripWeather();
                  setDepartureModalOpen(false);
                }}
              >
                Current Weather
              </Button>
              <Button
                className="flex-1 h-11 bg-cyan-500 hover:bg-cyan-600 font-oswald uppercase"
                onClick={handleSetDeparture}
                disabled={loadingForecast}
              >
                {loadingForecast ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Get Forecast
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="font-oswald uppercase text-red-400 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete Trip
            </DialogTitle>
          </DialogHeader>
          <div className="pt-4 space-y-4">
            <p className="text-gray-300">
              Are you sure you want to delete <span className="font-semibold text-white">"{trip.title}"</span>?
            </p>
            <p className="text-sm text-gray-500">
              This will permanently remove the trip and all its stops. This action cannot be undone.
            </p>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1 border-white/20"
                onClick={() => setDeleteModalOpen(false)}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-delete-btn"
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={handleDeleteTrip}
                disabled={deleting}
              >
                {deleting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TripDetail;
